import { useEffect, useState, useRef } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import debounce from 'lodash.debounce';

// custom hooks to instantiate responsive chart size / update on resize
const combineChartDimensions = (dimensions) => {
  const parsedDimensions = {
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    ...dimensions,
  };

  return {
    ...parsedDimensions,
    boundedHeight: Math.max(
      parsedDimensions.height -
        parsedDimensions.marginTop -
        parsedDimensions.marginBottom,
      0
    ),
    boundedWidth: Math.max(
      parsedDimensions.width -
        parsedDimensions.marginLeft -
        parsedDimensions.marginRight,
      0
    ),
  };
};

const useChartDimensions = (passedSettings, debounceValue = 0) => {
  const ref = useRef();
  const dimensions = combineChartDimensions(passedSettings);

  const [width, changeWidth] = useState(0);
  const [height, changeHeight] = useState(0);

  useEffect(() => {
    if (dimensions.width && dimensions.height) return [ref, dimensions];

    const element = ref.current;
    const resizeObserver = new ResizeObserver(
      debounce((entries) => {
        if (!Array.isArray(entries)) return;
        if (!entries.length) return;

        const entry = entries[0];

        if (width !== entry.contentRect.width)
          changeWidth(entry.contentRect.width);
        if (height !== entry.contentRect.height)
          changeHeight(entry.contentRect.height);
      }, debounceValue)
    );
    resizeObserver.observe(element);

    return () => resizeObserver.unobserve(element);
  }, [passedSettings, height, width, dimensions, debounceValue]);

  const newSettings = combineChartDimensions({
    ...dimensions,
    width: dimensions.width || width,
    height: dimensions.height || height,
  });

  return [ref, newSettings];
};

export { useChartDimensions, combineChartDimensions };
